/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useMemo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';
import { IntlProvider } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';

import { SuggestionIcon } from './SuggestionIcon';

const suggestionIconScaleVar = '--suggestion-icon-scale';
const suggestionIconRotateVar = '--suggestion-icon-rotate';
const suggestionIconOpacityVar = '--suggestion-icon-opacity';

const styles = cssMap({
	container: {
		position: 'absolute',
		transform: 'translateX(100%)',
		pointerEvents: 'all',
	},
	button: {
		display: 'block',
		[suggestionIconScaleVar]: '1',
		[suggestionIconRotateVar]: '0deg',
		[suggestionIconOpacityVar]: '1',
		'&:hover': {
			// for some reason we need a non-variable property here otherwise there's a type error
			display: 'block',
			[suggestionIconScaleVar]: 'calc(4/3)',
			[suggestionIconRotateVar]: '90deg',
			[suggestionIconOpacityVar]: '0',
		},
	},
	svgContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: 'transform 240ms ease-out',
		transformOrigin: 'center center',
		transform: `scale(var(${suggestionIconScaleVar})) rotate(var(${suggestionIconRotateVar}))`,
	},
	baseIconColorForeignObject: {
		height: '100%',
		width: '100%',
		position: 'absolute',
		top: 0,
		left: 0,
		transition: 'opacity 240ms ease-out',
	},
	iconColor: {
		backgroundColor: token('color.icon.subtle'),
		opacity: `var(${suggestionIconOpacityVar})`,
	},
	iconColorHover: {
		opacity: `calc(1 - var(${suggestionIconOpacityVar}))`,
		background:
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			'conic-gradient(from 90deg, rgba(252, 167, 0, 1) 0deg, rgba(252, 167, 0, 1) 90deg, rgba(106, 153, 34, 1) 90deg, rgba(106, 153, 34, 1) 180deg, rgba(24, 104, 219, 1) 180deg, rgba(24, 104, 219, 1) 270deg, rgba(175, 89, 224, 1) 270deg, rgba(175, 89, 224, 1) 360deg)',
	},
});

const AnimatedIcon = () => (
	<span css={styles.svgContainer}>
		<SuggestionIcon label="" size="small">
			<div css={[styles.baseIconColorForeignObject, styles.iconColor]} />
			<div css={[styles.baseIconColorForeignObject, styles.iconColorHover]} />
		</SuggestionIcon>
	</span>
);

export function SuggestionIconButton({
	leftAdjustment,
	suggestion,
	suggestionsCount,
	isSelected,
	api,
	intl,
	topAdjustment,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	intl?: IntlShape;
	isSelected: boolean;
	leftAdjustment?: number;
	suggestion: Suggestion;
	suggestionsCount: number;
	// Used to visually adjust the position so it lines up with block controls
	topAdjustment?: number;
}): JSX.Element {
	const { baseLocalIdBySuggestionId } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => {
			const { baseLocalIdBySuggestionId } = state?.aiSuggestionsState ?? {};

			return {
				baseLocalIdBySuggestionId,
			};
		},
	);

	const onSelectSuggestion = () => {
		api?.aiSuggestions?.actions.selectSuggestion(suggestion.id, { interactionPoint: 'card' });
	};

	const setHoveredSuggestion = (id: string | undefined) => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, { action: 'HOVER_SUGGESTION', id });
			return tr;
		});
	};

	const tooltipContent = useMemo(
		() =>
			suggestionsCount > 1
				? intl?.formatMessage(aiSuggestionsMessages.iconButtonSuggestionsCountLabel, {
						count: suggestionsCount,
					}) || suggestion.shortDescription
				: suggestion.shortDescription,
		[intl, suggestion.shortDescription, suggestionsCount],
	);

	return (
		<IntlProvider
			defaultLocale={intl?.defaultLocale || 'en-US'}
			locale={intl?.locale || 'en-US'}
			messages={intl?.messages}
			formats={intl?.formats}
		>
			<span
				css={styles.container}
				style={{
					anchorName: `--button-${baseLocalIdBySuggestionId?.get(suggestion.id)}`,
					top: topAdjustment ?? 0,
					right: `calc(${token('space.negative.300')} + ${token('space.negative.300')} - ${
						leftAdjustment ?? 0
					}px)`,
				}}
			>
				{
					// We keep the anchorName span rendered so that the suggestion card can reference it
					!isSelected && (
						<Tooltip content={tooltipContent} position="right">
							<span
								css={styles.button}
								onMouseEnter={() => setHoveredSuggestion(suggestion.id)}
								onMouseLeave={() => setHoveredSuggestion(undefined)}
								onFocus={() => setHoveredSuggestion(suggestion.id)}
								onBlur={() => setHoveredSuggestion(undefined)}
							>
								<IconButton
									shape="circle"
									type="button"
									label={tooltipContent}
									onClick={onSelectSuggestion}
									appearance="subtle"
									spacing="compact"
									icon={AnimatedIcon}
								/>
							</span>
						</Tooltip>
					)
				}
			</span>
		</IntlProvider>
	);
}
